/**
 * LX-01 Lexical fingerprint (INV-CH-LX-01; change C-LX01-ADV).
 *
 * Events: TELLS weighted hits (word set over the seed's tokenizer) plus the intensifier-adverb
 * saturation term `1.0·max(0, advRate − 20)` per 1k words, where advRate is the share of non-excluded
 * `-ly` adverbs per 1k prose words (RX-LX01-ADV). The seed added its adverb excess (already a per-1k
 * quantity, scaled 0.35) to a raw count before dividing by length; the term is now a proper rate.
 * The document-level excess is distributed over the adverbs (each carries weight excess/advCount),
 * so every adverb is an event of fractional weight and K/E·1000 equals the specified density.
 * Exposure: prose words. Ramp: the seed's, verbatim.
 */
import { safeScan } from '../lexicons/regex-list.ts';
import {
	Tally,
	compiledEnglish,
	fmt,
	line,
	nativeMeasure,
	proseBlocks,
	rateDef,
	wordExposure,
} from './a-shared.ts';
import { makeEvidence, rateChannel } from './framework.ts';
import type { ChannelImpl, Measurement, RunContext } from './types.ts';

const ADV_THRESHOLD = 20;
const ADV_FACTOR = 1.0;

export const lx01Def = rateDef({
	id: 'LX-01',
	name: 'Lexical fingerprint',
	family: 'VOCAB',
	features: [
		{ id: 'tells', label: 'TELLS vocabulary (weighted)' },
		{ id: 'adverbs', label: 'Intensifier-adverb excess' },
	],
	oneWay: true,
	minExposure: 300,
	exposureUnit: 'words',
	lang: 'native',
	ramp: [
		[0, 4],
		[2, 22],
		[5, 48],
		[10, 72],
		[18, 92],
		[30, 98],
	],
	unitScale: 1000,
	unitLabel: 'weighted hits per 1k words',
	phi0: 1.5,
	weight: 1.7,
	nd: 0.5,
	changes: ['C-LX01-ADV'],
});

function measure(ctx: RunContext): Measurement {
	const { doc } = ctx;
	const lex = compiledEnglish();
	const tellData = lex.tells;
	const P = doc.paragraphs.count;
	const k = new Float64Array(P);
	const kTell = new Float64Array(P);
	const nAdv = new Float64Array(P);
	const e = wordExposure(doc);
	const ev = makeEvidence();
	const tells = new Tally();
	const advRe = lex.channels['ch-lx01'].get('adverbCandidate');
	const excludeRe = lex.channels['ch-lx01'].get('adverbExclude');
	const advStart: number[] = [];
	const advEnd: number[] = [];
	const advPara: number[] = [];
	const advWords: string[] = [];
	let words = 0;
	for (const b of proseBlocks(doc)) {
		words += e[b.p] as number;
		tellData.scan(b.text, b.base, (s, en, w, x) => {
			k[b.p] = (k[b.p] as number) + x;
			kTell[b.p] = (kTell[b.p] as number) + x;
			ev.add(s, en, 0, x);
			tells.add(w);
		});
		if (!b.text.includes('ly')) continue;
		safeScan(advRe, b.text, b.base, (s, en, m) => {
			const w = m[0];
			if (excludeRe.test(w)) return;
			advStart.push(s);
			advEnd.push(en);
			advPara.push(b.p);
			advWords.push(w.toLowerCase());
		});
	}
	// Adverb saturation: excess over the human base rate, spread over the adverbs.
	const advCount = advStart.length;
	const denom = Math.max(1, words);
	const advRate = (advCount / denom) * 1000;
	const excessRate = Math.max(0, advRate - ADV_THRESHOLD) * ADV_FACTOR;
	const excessEvents = (excessRate * words) / 1000;
	// bench measurement (BENCH-V2-SPEC §5.1): the document's per-adverb weight is frozen at baseline
	const advW = ctx.frozen ? ctx.frozen.lx01AdvW : advCount > 0 ? excessEvents / advCount : 0;
	for (let i = 0; i < advCount; i++)
		nAdv[advPara[i] as number] = (nAdv[advPara[i] as number] as number) + 1;
	if (advW > 0) {
		for (let i = 0; i < advCount; i++) {
			const p = advPara[i] as number;
			k[p] = (k[p] as number) + advW;
			ev.add(advStart[i] as number, advEnd[i] as number, 1, advW);
		}
	}
	const summary: string[] = [];
	if (advW > 0)
		summary.push(
			line(
				`${advCount} intensifier adverbs · ${fmt(advRate, 0)}/1000w`,
				`${advWords.slice(0, 8).join(', ')} — saturation above human base rate`
			)
		);
	for (const [w, n] of tells.top(14)) summary.push(line(w, `${n}× · weight ${tellData.weight(w)}`));
	let tellK = 0;
	for (const [w, n] of tells.top()) tellK += n * tellData.weight(w);
	return {
		k,
		e,
		evidence: ev,
		summary,
		aux: { advW, kTell, nAdv },
		features: {
			tellsPerK: (tellK / denom) * 1000,
			advPerK: advRate,
			advExcessPerK: excessRate,
		},
	};
}

export const lx01: ChannelImpl = rateChannel(lx01Def, nativeMeasure(measure));
export { lx01Def as def, lx01 as impl };
